// src/ProjectSentiment/Chart/useUserSentimentSeries.ts
import { useMemo } from "react";
import {
  subHours,
  startOfHour,
  startOfDay,
  startOfMonth,
  endOfMonth,
  eachHourOfInterval,
  eachDayOfInterval,
  format,
} from "date-fns";
import { useUserMessages } from "./useUserMessages";
import type { ScoredMsg } from "./sentimentLocal";
import { Pt, Mode, SeriesOptions } from "./useNetSentimentSeries";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/**
 * Mirrors useNetSentimentSeries but sources messages
 * from useUserMessages instead of useChannelMessages.
 */
export const useUserSentimentSeries = (
  userId: string | null,
  mode: Mode,
  options: SeriesOptions = {}
): Pt[] => {
  const { msgs } = useUserMessages(userId);

  return useMemo<Pt[]>(() => {
    if (!msgs.length) return [];
    const now = new Date();
    const year = options.year ?? now.getFullYear();
    const month = options.month ?? now.getMonth() + 1;
    
    // Initialize buckets with separate positive/negative arrays and user tracking
    const buckets: Record<string, {
      positive: { score: number; user: string }[];
      negative: { score: number; user: string }[];
    }> = {};
    
    const ensure = (k: string) => {
      if (!buckets[k]) {
        buckets[k] = {
          positive: [],
          negative: []
        };
      }
      return buckets[k];
    };

    switch (mode) {
      case "today": {
        const start = subHours(now, 23);
        eachHourOfInterval({ start, end: now }).forEach((h) =>
          ensure(format(h, "HH:00"))
        );
        msgs.forEach((m: ScoredMsg) => {
          const d = new Date(m.createdAt);
          if (d < start || d > now) return;
          const key = format(startOfHour(d), "HH:00");
          const bucket = ensure(key);
          if (m.score > 0) {
            bucket.positive.push({ score: m.score, user: m.userId });
          } else if (m.score < 0) {
            bucket.negative.push({ score: m.score, user: m.userId });
          }
        });
        break;
      }
      case "weekly": {
        // Define four buckets: days 1–7 => "W1", 8–14 => "W2", 15–21 => "W3", 22–end => "W4"
        const s = new Date(year, month - 1, 1); // first of month
        const lastDay = new Date(year, month, 0).getDate(); // e.g. 30 or 31
        const weekRanges = [
          { label: "W1", start: 1, end: 7 },
          { label: "W2", start: 8, end: 14 },
          { label: "W3", start: 15, end: 21 },
          { label: "W4", start: 22, end: lastDay },
        ];
        // initialize buckets
        weekRanges.forEach((w) => ensure(w.label));

        // assign each message into its week
        msgs.forEach((m: ScoredMsg) => {
          const d = new Date(m.createdAt);
          if (d.getFullYear() !== year || d.getMonth() + 1 !== month) return;
          const day = d.getDate();
          const wk = weekRanges.find((w) => day >= w.start && day <= w.end);
          if (wk) {
            const bucket = ensure(wk.label);
            if (m.score > 0) {
              bucket.positive.push({ score: m.score, user: m.userId });
            } else if (m.score < 0) {
              bucket.negative.push({ score: m.score, user: m.userId });
            }
          }
        });
        break;
      }
      case "monthly": {
        const start = startOfMonth(new Date(year, month - 1));
        const end = endOfMonth(start);
        eachDayOfInterval({ start, end }).forEach((d) =>
          ensure(d.getDate().toString())
        );
        msgs.forEach((m: ScoredMsg) => {
          const d = new Date(m.createdAt);
          if (d.getFullYear() !== year || d.getMonth() + 1 !== month) return;
          const bucket = ensure(d.getDate().toString());
          if (m.score > 0) {
            bucket.positive.push({ score: m.score, user: m.userId });
          } else if (m.score < 0) {
            bucket.negative.push({ score: m.score, user: m.userId });
          }
        });
        break;
      }
      case "yearly": {
        // 1) Pre-create an empty bucket for every month
        MONTHS.forEach((mn) => ensure(mn));

        // 2) Fill with any messages from that year
        msgs.forEach((m) => {
          const d = new Date(m.createdAt);
          if (d.getFullYear() !== year) return;
          const label = MONTHS[d.getMonth()];
          const bucket = ensure(label);
          if (m.score > 0) {
            bucket.positive.push({ score: m.score, user: m.userId });
          } else if (m.score < 0) {
            bucket.negative.push({ score: m.score, user: m.userId });
          }
        });
        break;
      }
      case "overall":
      default: {
        msgs.forEach((m) => {
          const d = new Date(m.createdAt);
          const key = d.getFullYear().toString();
          const bucket = ensure(key);
          if (m.score > 0) {
            bucket.positive.push({ score: m.score, user: m.userId });
          } else if (m.score < 0) {
            bucket.negative.push({ score: m.score, user: m.userId });
          }
        });
      }
    }

    return Object.entries(buckets)
      .map(([label, bucket]) => {
        const posAvg = bucket.positive.length ? 
          bucket.positive.reduce((a, v) => a + v.score, 0) / bucket.positive.length : 0;
        const negAvg = bucket.negative.length ? 
          bucket.negative.reduce((a, v) => a + v.score, 0) / bucket.negative.length : 0;
        
        return {
          label,
          value: posAvg + negAvg, // Keep for backward compatibility
          positiveValue: posAvg,
          negativeValue: negAvg,
          users: {
            positive: [],
            negative: []
          }
        };
      })
      .sort((a, b) => {
        // 1. Numeric labels (days/weeks) – unchanged
        const na = parseFloat(a.label), nb = parseFloat(b.label);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;

        // 2. Month names in calendar order
        const ia = MONTHS.indexOf(a.label), ib = MONTHS.indexOf(b.label);
        if (ia !== -1 && ib !== -1) return ia - ib;

        // 3. Fallback to lexical
        return a.label.localeCompare(b.label);
      });
  }, [msgs, mode, options.year, options.month]);
};
